import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from 'hooks';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
}: BottomSheetProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    restoreFocusRef.current = document.activeElement as HTMLElement;
    panelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      restoreFocusRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-end justify-center sm:items-center"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50 fade-in"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="relative w-full sm:w-[420px] sm:max-w-[calc(100vw_-_32px)] max-h-[85svh] overflow-y-auto bg-white rounded-t-[20px] sm:rounded-[20px] shadow-xl focus:outline-none sheet-in inset-b-safe"
      >
        <div className="sm:hidden pt-[10px] pb-[2px] flex justify-center">
          <div className="h-[4px] w-[36px] rounded-full bg-[#D5D9DF]" />
        </div>
        {(title || description) && (
          <div className="px-5 pt-4 pb-2">
            {title && (
              <p className="text-[17px] font-[600] text-[#2E435F]">{title}</p>
            )}
            {description && (
              <p className="text-[14px] text-[#828E9F] mt-1">{description}</p>
            )}
          </div>
        )}
        {children && (
          <div className="px-2 pb-2 sm:px-5 sm:pb-4">{children}</div>
        )}
        {footer && <div className="px-5 pb-5 pt-1">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};
